import { useEffect, useRef, useState } from "react";
import { Volume2, VolumeX, SkipForward } from "lucide-react";

/**
 * BESPOKE ambient music player — with volume slider and minimize-to-dot behavior.
 *
 * Real audio tracks (public-domain / CC-BY): Erik Satie Gymnopédies + Meditation Impromptu.
 * After 8 s of no interaction the whole player collapses to a tiny throbbing champagne
 * dot; any hover / touch expands it again. Volume slider appears on hover / focus.
 */
const TRACKS = [
  { file: "/music/gymnopedie.mp3",   title: "Gymnopédie No. 1 · Erik Satie" },
  { file: "/music/gymnopedie-2.mp3", title: "Gymnopédie No. 2 · Erik Satie" },
  { file: "/music/meditation.mp3",   title: "Meditation Impromptu · Kevin MacLeod" },
];
const FADE_MS = 900;
const IDLE_MS = 8000;

export default function SoundToggle() {
  const [on, setOn] = useState(false);
  const [trackIdx, setTrackIdx] = useState(0);
  const [volume, setVolume] = useState(0.32);
  const [minimized, setMinimized] = useState(false);
  const [hover, setHover] = useState(false);
  const audioRef = useRef(null);
  const fadeTimer = useRef(null);
  const idleTimer = useRef(null);

  // Hydrate from localStorage
  useEffect(() => {
    try {
      setOn(localStorage.getItem("bes_sound") === "1");
      const t = parseInt(localStorage.getItem("bes_track"), 10);
      if (!isNaN(t) && t >= 0 && t < TRACKS.length) setTrackIdx(t);
      const v = parseFloat(localStorage.getItem("bes_volume"));
      if (!isNaN(v) && v >= 0 && v <= 1) setVolume(v);
    } catch { /* ignore */ }
  }, []);

  useEffect(() => { try { localStorage.setItem("bes_sound", on ? "1" : "0"); } catch { /* ignore */ } document.documentElement.dataset.soundOn = on ? "true" : "false"; }, [on]);
  useEffect(() => { try { localStorage.setItem("bes_track", String(trackIdx)); } catch { /* ignore */ } }, [trackIdx]);
  useEffect(() => { try { localStorage.setItem("bes_volume", String(volume)); } catch { /* ignore */ } }, [volume]);

  // Idle → minimize (only when playing and not currently hovered)
  useEffect(() => {
    if (!on || hover) { setMinimized(false); if (idleTimer.current) clearTimeout(idleTimer.current); return; }
    idleTimer.current = setTimeout(() => setMinimized(true), IDLE_MS);
    return () => { if (idleTimer.current) clearTimeout(idleTimer.current); };
  }, [on, hover, volume, trackIdx]);

  // Fade helper
  function fadeTo(target, ms) {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    const start = audio.volume;
    const t0 = performance.now();
    if (fadeTimer.current) cancelAnimationFrame(fadeTimer.current);
    function step(t) {
      const p = Math.min(1, (t - t0) / ms);
      const v = start + (target - start) * p;
      audio.volume = Math.max(0, Math.min(1, v));
      if (p < 1) fadeTimer.current = requestAnimationFrame(step);
    }
    fadeTimer.current = requestAnimationFrame(step);
  }

  // Play / pause & track change
  useEffect(() => {
    if (!audioRef.current) return;
    const audio = audioRef.current;
    audio.volume = 0;
    if (on) {
      audio.play().then(() => fadeTo(volume, FADE_MS)).catch(() => setOn(false));
    } else {
      fadeTo(0, FADE_MS / 1.4);
      const t = setTimeout(() => audio.pause(), FADE_MS);
      return () => clearTimeout(t);
    }
  }, [on, trackIdx]); // eslint-disable-line react-hooks/exhaustive-deps

  // React to slider changes while playing
  useEffect(() => {
    if (!audioRef.current || !on) return;
    fadeTo(volume, 200);
  }, [volume, on]);

  function onEnded() { setTrackIdx((i) => (i + 1) % TRACKS.length); }
  function nextTrack(e) { e.stopPropagation(); setTrackIdx((i) => (i + 1) % TRACKS.length); }

  const track = TRACKS[trackIdx];
  const collapsed = on && minimized && !hover;

  return (
    <>
      <audio ref={audioRef} src={track.file} preload="none" onEnded={onEnded} loop={false} data-testid="ambient-audio" />
      <div
        className="fixed bottom-6 left-6 z-50 flex items-center gap-2"
        onMouseEnter={() => setHover(true)}
        onMouseLeave={() => setHover(false)}
        data-testid="music-player"
      >
        {/* Collapsed dot */}
        {collapsed && (
          <button
            onClick={() => setMinimized(false)}
            data-testid="sound-dot"
            aria-label="Expand music player"
            title={`Now playing · ${track.title}`}
            className="w-3.5 h-3.5 transition-all duration-500"
            style={{
              borderRadius: "var(--r-full)",
              background: "var(--gold)",
              boxShadow: "0 0 12px rgba(198,168,124,0.7)",
              animation: "dotPulse 2.4s ease-in-out infinite",
            }}
          />
        )}

        {/* Expanded pill */}
        {!collapsed && (
          <>
            <button
              onClick={() => setOn((v) => !v)}
              data-testid="sound-toggle"
              data-cursor="hover"
              data-cursor-label={on ? "Mute" : "Play"}
              aria-label={on ? "Pause music" : "Play music"}
              title={on ? `Now playing · ${track.title}   (music: incompetech.com · CC-BY)` : "Play ambient music (public-domain piano)"}
              className="h-12 flex items-center gap-3 px-4 transition-all duration-500"
              style={{
                borderRadius: "var(--r-full)",
                border: `1px solid ${on ? "var(--gold)" : "var(--line-gold)"}`,
                background: on ? "rgba(198,168,124,0.14)" : "rgba(11,11,13,0.72)",
                color: on ? "var(--gold)" : "var(--text-2)",
                backdropFilter: "blur(12px)",
                boxShadow: on ? "0 0 24px rgba(198,168,124,0.22)" : "0 4px 18px rgba(0,0,0,0.4)",
              }}
            >
              {on ? <Volume2 size={15} strokeWidth={1.5} /> : <VolumeX size={15} strokeWidth={1.5} />}
              <span className="font-mono text-[9px] uppercase tracking-[0.36em] whitespace-nowrap">
                {on ? "Music On" : "Music"}
              </span>
              {on && (
                <span className="flex items-end gap-0.5 h-3 ml-1" aria-hidden>
                  {[0, 1, 2].map((i) => (
                    <span key={i} className="block w-[2px] rounded-full"
                      style={{ background: "var(--gold)", animation: `soundBar 1.2s ease-in-out ${i * 0.15}s infinite` }} />
                  ))}
                </span>
              )}
            </button>

            {on && (
              <>
                {/* Volume slider */}
                <div
                  className="h-12 flex items-center gap-3 px-4 transition-all duration-500"
                  style={{
                    borderRadius: "var(--r-full)",
                    border: "1px solid var(--line-gold)",
                    background: "rgba(11,11,13,0.72)",
                    backdropFilter: "blur(12px)",
                    width: hover ? 160 : 44,
                    overflow: "hidden",
                  }}
                  data-testid="sound-volume-wrap"
                >
                  {hover ? (
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.01}
                      value={volume}
                      onChange={(e) => setVolume(parseFloat(e.target.value))}
                      aria-label="Volume"
                      data-testid="sound-volume"
                      className="bes-volume"
                      style={{ "--v": `${Math.round(volume * 100)}%` }}
                    />
                  ) : (
                    <div className="w-4 h-4 flex items-center justify-center">
                      <Volume2 size={13} strokeWidth={1.5} style={{ color: "var(--gold)", opacity: 0.7 }} />
                    </div>
                  )}
                </div>
                {/* Skip */}
                <button
                  onClick={nextTrack}
                  data-testid="sound-next"
                  data-cursor="hover"
                  data-cursor-label="Next"
                  aria-label="Next track"
                  title={`Now playing: ${track.title}`}
                  className="h-12 w-12 flex items-center justify-center transition-all duration-500"
                  style={{
                    borderRadius: "var(--r-full)",
                    border: "1px solid var(--line-gold)",
                    background: "rgba(11,11,13,0.72)",
                    color: "var(--gold)",
                    backdropFilter: "blur(12px)",
                  }}
                >
                  <SkipForward size={14} strokeWidth={1.5} />
                </button>
              </>
            )}
          </>
        )}
      </div>
    </>
  );
}
